import { useEffect, useState, useCallback } from 'react';
import { fullRefuseCleanup, startPostHogTracking, stopPostHogTracking } from '../lib/cookies';

/** localStorage key holding the user's consent decision.
 *  Stored as a JSON string of shape { preferences: bool, analytics: bool, ts: number }
 *  or, for legacy installations, one of 'accepted' | 'refused' (auto-migrated). */
const KEY = 'fluo_cookie_consent';

const DEFAULT_CHOICES = { preferences: false, analytics: false };

const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

/** Read & normalise the stored decision. Returns:
 *  - `null` when the user hasn't decided yet
 *  - `{ preferences, analytics, ts }` otherwise */
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    // Legacy values
    if (raw === 'accepted') return { preferences: true, analytics: true, ts: 0 };
    if (raw === 'refused')  return { preferences: false, analytics: false, ts: 0 };
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    return {
      preferences: !!obj.preferences,
      analytics: !!obj.analytics,
      ts: obj.ts || 0,
    };
  } catch { return null; }
};

export const setConsentChoices = (choices) => {
  try {
    if (!choices) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify({ ...DEFAULT_CHOICES, ...choices, ts: Date.now() }));
  } catch { /* private mode / quota */ }
  notify();
};

export const useConsent = () => {
  const [value, setValue] = useState(() => getConsent());

  useEffect(() => {
    const fn = () => setValue(getConsent());
    listeners.add(fn);
    const onStorage = (e) => { if (e.key === KEY) fn(); };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(fn);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Apply side-effects whenever consent changes.
  //  - analytics ON  → opt PostHog in
  //  - analytics OFF → opt PostHog out
  //  - the FIRST time consent is recorded as refusal for any category, run a
  //    full cleanup so that any cookie/storage written before consent is wiped.
  useEffect(() => {
    if (!value) { stopPostHogTracking(); return; }
    if (value.analytics) startPostHogTracking();
    else stopPostHogTracking();
    if (!value.preferences && !value.analytics) {
      // Hard refusal — wipe everything non-essential.
      fullRefuseCleanup();
    } else if (!value.analytics) {
      // Partial: only analytics refused — still wipe analytics-related artefacts.
      stopPostHogTracking();
    }
  }, [value]);

  const acceptAll = useCallback(() => setConsentChoices({ preferences: true, analytics: true }), []);
  const refuseAll = useCallback(() => {
    fullRefuseCleanup();
    setConsentChoices({ preferences: false, analytics: false });
  }, []);
  const saveChoices = useCallback((choices) => {
    // If user un-checks both, treat it as refusal (and clean up).
    if (!choices?.preferences && !choices?.analytics) fullRefuseCleanup();
    setConsentChoices(choices);
  }, []);
  const reset = useCallback(() => setConsentChoices(null), []);

  return {
    consent: value,
    hasDecided: !!value,
    canStorePreferences: !!value?.preferences,
    canTrack: !!value?.analytics,
    acceptAll,
    refuseAll,
    saveChoices,
    reset,
  };
};
